import * as THREE from "three";
import { createXRRig } from "./xr/session";
import { InteractionSystem } from "./xr/input";
import { buildWorkshopEnvironment } from "./world/room";
import { createRoomPanelSystem } from "./interaction/RoomPanelSystem";
import {
  Workshop,
  roomChallenge,
  createOutletAssemblyStation,
} from "./workshop";

/**
 * Electrical garage — “find the fault.”
 *
 * Foundation owns session/rig/input lifecycle and the room-scale electrical
 * challenge. The room itself is the experiment (`roomChallenge`). Outlet
 * Assembly remains as a left-wall exposed repair bay. V/I/R and Series/Parallel
 * tables are not spawned.
 *
 * Non-energized training mockup only. Panel jumpers / LIGHT B switch / meter /
 * device response live in {@link createRoomPanelSystem}.
 *
 * Reset semantics:
 * - KeyR → re-apply the current authored fault (not player pose) and reset the
 *   active / nearest remaining station (outlet repair bay). Meter returns to dock.
 * - Outlet Reset plate → outlet station only.
 * - Panel NEW FAULT (`reset` mesh) → `roomChallenge.newFault()`.
 * - XR session start/end → clear sticky input + release holds on the outlet
 *   station and the room challenge; seated jumpers / locked wires persist
 *   until reset. A held meter drops in place (no dock snap) until newFault/KeyR.
 */
const container = document.getElementById("app");
if (!container) throw new Error("#app container not found");

const rig = createXRRig(container);
const env = buildWorkshopEnvironment(rig.scene);

const interaction = new InteractionSystem(rig);
const workshop = new Workshop();

const outlet = createOutletAssemblyStation({
  rig,
  interaction,
  parent: env.room,
});
workshop.register(outlet);

const roomPanel = createRoomPanelSystem({
  rig,
  interaction,
  env,
});

const onSessionBoundary = (): void => {
  // Drop holds before input.reset() clears squeeze (avoids accidental snap).
  workshop.releaseAllSessionHolds();
  roomChallenge.releaseSessionHolds();
  interaction.reset();
};
rig.renderer.xr.addEventListener("sessionstart", onSessionBoundary);
rig.renderer.xr.addEventListener("sessionend", () => {
  onSessionBoundary();
  rig.resetPlayerPose();
});

const _playerPos = new THREE.Vector3();

window.addEventListener("keydown", (e) => {
  if (e.code !== "KeyR" || e.repeat) return;
  roomChallenge.reset();
  workshop.resetActiveOrNearest(rig.player.getWorldPosition(_playerPos));
});

// Hidden debug advance (URL ?debug=1 only) — outlet station only.
const debugSteps =
  typeof location !== "undefined" &&
  new URLSearchParams(location.search).has("debug");
if (debugSteps) {
  window.addEventListener("keydown", (e) => {
    if (e.code !== "KeyN" || !e.shiftKey || e.repeat) return;
    const step = outlet.trainer.getStep();
    if (step === "place_wires") outlet.trainer.completeStep("place_wires");
    else if (step === "mount_receptacle") {
      outlet.trainer.completeStep("mount_receptacle");
    }
  });
}

const clock = new THREE.Clock();
rig.renderer.setAnimationLoop(() => {
  const dt = Math.min(clock.getDelta(), 0.1);
  interaction.update(dt);
  rig.player.getWorldPosition(_playerPos);
  workshop.update(dt, _playerPos);
  roomChallenge.update(dt);
  roomPanel.update(dt);
  rig.renderer.render(rig.scene, rig.camera);
});
